// NIGHT-DIRECTSTAFF-2026-10-19 §4: two leads that look like the same person (same phone, any formatting, or same email,
// any case) -- the "Looks like the same person" strip, and the merge.
//
// THE MERGE (leads.merged_into, migrations/2026-10-19-leads-merged-into.sql, filed for the hub) keeps the OLDER lead:
//   1. the older lead's BLANK fields (name, email, phone, discipline, state) are filled from the newer one -- nothing on
//      the older lead is overwritten;
//   2. the newer lead is marked merged_into = the older id, merged_at, merged_by -- never deleted. Every list and count
//      leaves it out; ?lead=<its id> opens the older lead (the redirect).
// Undo (for the rest of the session) reverses exactly those two writes. Both writes ask for the row back: with RLS on, an
// update the policy refuses is zero rows, not an error, and Matt must never be told it merged when nothing was written.
//
// What a lead merge does NOT move, and why: exams and the onboarding packet belong to the CANDIDATE, not to a lead (and
// the person's timeline already joins every lead, thread and candidate record by phone/email); the text threads are the
// agents window's own merge (agent_conversations.merged_into, their 2026-10-19 file); drafts and decisions keep the lead
// id they were made for and stay readable from it.

export const MERGE_COLUMNS = ['merged_into', 'merged_at', 'merged_by'];
export const FILL_FIELDS = ['name', 'email', 'phone', 'discipline', 'state'];

const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const national = (v) => { const d = String(v || '').replace(/\D/g, ''); return d.length === 11 && d.startsWith('1') ? d.slice(1) : d; };
const t = (iso) => { const x = new Date(iso).getTime(); return Number.isNaN(x) ? Infinity : x; };

export const isMerged = (l) => !!(l && s(l.merged_into));

/** Same person: the same 10-digit phone, or the same email (blank never matches). */
export function samePerson(a, b) {
  if (!a || !b || a.id === b.id) return false;
  const pa = national(a.phone), pb = national(b.phone);
  if (pa.length === 10 && pa === pb) return true;
  const ea = s(a.email).toLowerCase(), eb = s(b.email).toLowerCase();
  return !!ea && ea.includes('@') && ea === eb;
}
/** How they match, for the strip: 'phone' | 'email' | 'phone and email'. */
export function matchedOn(a, b) {
  const p = national(a.phone).length === 10 && national(a.phone) === national(b.phone);
  const e = !!s(a.email) && s(a.email).toLowerCase() === s(b.email).toLowerCase();
  return p && e ? 'phone and email' : p ? 'phone' : 'email';
}
/** The other (not merged) leads that look like the same person as `lead`, oldest first. */
export function lookAlikes(lead, leads) {
  return (leads || []).filter((l) => !isMerged(l) && samePerson(lead, l)).sort((a, b) => t(a.created_at) - t(b.created_at));
}
/** -> { older, newer } (created_at; a tie keeps the smaller id as the older, so the choice is stable). */
export function olderNewer(a, b) {
  const d = t(a.created_at) - t(b.created_at);
  return d < 0 || (d === 0 && String(a.id) < String(b.id)) ? { older: a, newer: b } : { older: b, newer: a };
}
/** The blanks on the older lead that the newer one can fill. -> { patch, filled: [field] } */
export function fillFrom(older, newer) {
  const patch = {};
  for (const f of FILL_FIELDS) if (!s(older[f]) && s(newer[f])) patch[f] = newer[f];
  return { patch, filled: Object.keys(patch) };
}

const one = async (q, what) => {
  const { data, error } = await q;
  if (error) throw new Error(error.message || `Could not save the ${what}`);
  if (!Array.isArray(data) || data.length !== 1) throw new Error(`Not saved: the database did not accept the ${what} (are you signed in?)`);
};

/** Merge newer into older. -> { older, newer, filled, fillPatch, mergePatch } -- what Undo needs. */
export async function mergeLeads(client, a, b, by = '', now = () => new Date()) {
  const { older, newer } = olderNewer(a, b);
  const { patch: fillPatch, filled } = fillFrom(older, newer);
  if (filled.length) await one(client.from('leads').update(fillPatch).eq('id', older.id).select('id'), 'filled-in details');
  const mergePatch = { merged_into: older.id, merged_at: now().toISOString(), merged_by: String(by || '').slice(0, 320) || null };
  await one(client.from('leads').update(mergePatch).eq('id', newer.id).select('id'), 'merge');
  return { older, newer, filled, fillPatch, mergePatch };
}
/** Undo a merge made this session: the newer lead stands alone again; the filled blanks go back to blank. */
export async function undoMerge(client, done) {
  await one(client.from('leads').update({ merged_into: null, merged_at: null, merged_by: null }).eq('id', done.newer.id).select('id'), 'undo');
  if (done.filled.length) {
    const blank = Object.fromEntries(done.filled.map((f) => [f, null]));
    await one(client.from('leads').update(blank).eq('id', done.older.id).select('id'), 'undo of the filled-in details');
  }
}

/** One probe: is leads.merged_into there? Anything that errors reads as absent (Merge hidden, the strip still shown). */
export async function probeMergeColumns(client) {
  try { const { error } = await client.from('leads').select(MERGE_COLUMNS.join(','), { head: true }).limit(0); return !error; } catch { return false; }
}

/** The ids of (not merged) leads that have at least one look-alike -- one pass, for the list's "same person?" tag. */
export function dupIds(leads) {
  const by = new Map();
  const add = (k, id) => { if (!by.has(k)) by.set(k, new Set()); by.get(k).add(id); };
  for (const l of leads || []) {
    if (!l || isMerged(l)) continue;
    const p = national(l.phone); if (p.length === 10) add(`p:${p}`, l.id);
    const e = s(l.email).toLowerCase(); if (e.includes('@')) add(`e:${e}`, l.id);
  }
  const out = new Set();
  for (const ids of by.values()) if (ids.size > 1) for (const id of ids) out.add(id);
  return out;
}
